import React from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowUp, CornerDownRight, Diamond, ListIndentDecrease, ListIndentIncrease, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useGanttStore } from '../store/ganttStore'
import type { RowOps } from './row-ops'

interface Props { ops: RowOps }

/**
 * Toolbar editing group (spec B §9.5, Task 16): rendered by `detail/index.tsx` through
 * `GanttToolbar`'s `editingSlot` when `canEdit && !readOnly`. Structural ops (add/indent/outdent/
 * move/delete) are gated on the store's `pendingCount` in addition to `ops`'s own `can*` flags, so
 * the group can't be clicked twice while a previous structural mutation is still in flight.
 */
const GanttEditingGroup: React.FC<Props> = ({ ops }) => {
  const { t } = useTranslation()
  const busy = useGanttStore((s) => s.pendingCount) > 0

  const item = (
    key: string,
    Icon: React.ComponentType<{ className?: string }>,
    onClick: () => void,
    disabled: boolean,
    destructive = false,
  ) => (
    <Tooltip key={key}>
      <TooltipTrigger asChild>
        <span>
          <Button
            type="button"
            variant={destructive && !disabled ? 'destructive' : 'outline'}
            size="sm"
            className="h-8 w-8 p-0"
            disabled={disabled}
            onClick={onClick}
            aria-label={t(key)}
          >
            <Icon className="h-4 w-4" />
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent>{t(key)}</TooltipContent>
    </Tooltip>
  )

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex items-center gap-1">
        {/* Review fix (Important #4, spec §10): Add activity/milestone/sub-activity need `canCreate`,
            not just `canEdit` — hidden (not disabled) for a member with EDIT but not CREATE. */}
        {ops.canAdd && item('Schedule_op_add', Plus, () => ops.addActivity('task'), busy)}
        {ops.canAdd && item('Schedule_op_add_milestone', Diamond, () => ops.addActivity('milestone'), busy)}
        {ops.canAdd && item('Schedule_op_add_child', CornerDownRight, () => ops.addActivity('child'), busy)}
        {item('Schedule_op_indent', ListIndentIncrease, ops.indent, busy || !ops.canIndent)}
        {item('Schedule_op_outdent', ListIndentDecrease, ops.outdent, busy || !ops.canOutdent)}
        {item('Schedule_op_up', ArrowUp, ops.moveUp, busy || !ops.canMoveUp)}
        {item('Schedule_op_down', ArrowDown, ops.moveDown, busy || !ops.canMoveDown)}
        {item('Schedule_op_delete', Trash2, ops.remove, busy || !ops.canRemove, true)}
      </div>
    </TooltipProvider>
  )
}

export default GanttEditingGroup
